"use client";

import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { cn } from "./cn";
import { Field } from "./Field";

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectProps {
  label: string;
  helper?: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
  emptyLabel?: string;
  options: MultiSelectOption[];
  values: string[];
  onValuesChange: (values: string[]) => void;
  disabled?: boolean;
  id?: string;
}

function summarize(selected: MultiSelectOption[], placeholder: string): string {
  if (selected.length === 0) return placeholder;
  if (selected.length <= 2) return selected.map((option) => option.label).join(", ");
  return `${selected.length} selecionados`;
}

/** Multiple selection with an accessible running count instead of dozens of chips. */
export function MultiSelect({
  label,
  helper,
  error,
  required,
  placeholder = "Selecione",
  emptyLabel = "Nenhuma opção",
  options,
  values,
  onValuesChange,
  disabled,
  id,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const listId = useId();

  const selected = useMemo(
    () => options.filter((option) => values.includes(option.value)),
    [options, values],
  );

  function toggle(value: string) {
    if (values.includes(value)) {
      onValuesChange(values.filter((item) => item !== value));
    } else {
      onValuesChange([...values, value]);
    }
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlighted((index) => Math.min(index + 1, options.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlighted((index) => Math.max(index - 1, 0));
    } else if (event.key === "Home") {
      event.preventDefault();
      setHighlighted(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setHighlighted(options.length - 1);
    } else if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      const option = options[highlighted];
      if (option) toggle(option.value);
    }
  }

  return (
    <Field label={label} helper={helper} error={error} required={required} htmlFor={id}>
      {({ fieldId, describedBy }) => (
        <Popover.Root open={open} onOpenChange={setOpen}>
          <Popover.Trigger asChild>
            <button
              id={fieldId}
              type="button"
              aria-haspopup="listbox"
              aria-expanded={open}
              aria-controls={listId}
              aria-describedby={describedBy}
              disabled={disabled}
              className="ui-select-trigger"
            >
              <span
                className={cn(
                  "ui-multiselect-summary",
                  selected.length === 0 && "ui-combobox-placeholder",
                )}
              >
                {summarize(selected, placeholder)}
              </span>
              <ChevronDown size={16} className="ui-select-icon" aria-hidden="true" />
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              className="ui-combobox-content"
              sideOffset={4}
              align="start"
              onOpenAutoFocus={(event) => {
                event.preventDefault();
                setHighlighted(0);
                (event.currentTarget as HTMLElement).focus();
              }}
            >
              <ul
                id={listId}
                role="listbox"
                aria-multiselectable="true"
                tabIndex={0}
                className="ui-combobox-list"
                onKeyDown={onKeyDown}
                aria-activedescendant={
                  options[highlighted] ? `${listId}-${options[highlighted].value}` : undefined
                }
              >
                {options.length === 0 ? (
                  <li className="ui-combobox-empty">{emptyLabel}</li>
                ) : (
                  options.map((option, index) => {
                    const checked = values.includes(option.value);
                    return (
                      <li
                        key={option.value}
                        id={`${listId}-${option.value}`}
                        role="option"
                        aria-selected={checked}
                        data-selected={checked}
                        data-highlighted={index === highlighted}
                        className="ui-combobox-item"
                        onMouseEnter={() => setHighlighted(index)}
                        onClick={() => toggle(option.value)}
                      >
                        <span className="ui-multiselect-box" data-checked={checked} aria-hidden="true">
                          {checked ? <Check size={12} /> : null}
                        </span>
                        <span className="ui-select-item-text">{option.label}</span>
                      </li>
                    );
                  })
                )}
              </ul>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      )}
    </Field>
  );
}
